import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { withBoardAuth } from '@generated/server/boardAuth.server';
import { requireStaff } from '@generated/server/staff.server';
import { fillFromContracts, rememberSkip } from '@generated/server/contracts.server';
import { AdListBoard, PortalOrdersBoard, October2026RedHookBoard } from '@api/BoardSDK';

const PAPER = z.enum(['Red Hook Star-Revue', 'The Village Star-Revue']);
const SECTION = z.enum(['ROP', 'Arts', 'Special Section']);
const COPY = z.enum(['Need Copy', 'Have Copy', 'Same as Last Month']);
const BILLED = z.enum(['Not Billed', 'Invoiced', 'Paid']);
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function addMonths(label: string, n: number): string {
  const [m, y] = label.trim().split(/\s+/);
  const idx = MONTHS.indexOf(m ?? '');
  const year = Number(y);
  if (idx < 0 || !year) return label;
  const t = idx + n;
  return `${MONTHS[((t % 12) + 12) % 12]} ${year + Math.floor(t / 12)}`;
}

const COLS = ['issueMonth', 'paper', 'adSize', 'cost', 'section', 'specialSectionName', 'adCopy', 'billed', 'notes', 'portalOrderId'] as const;

export const getAdList = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), month: z.string().min(3), paper: PAPER.optional(), cursor: z.string().optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    if (!data.cursor) { try { await fillFromContracts(data.month); } catch (e) { console.error('[adlist] contract fill failed', e); } }
    const board = new AdListBoard();
    const list = data.cursor
      ? board.items().withPagination({ cursor: data.cursor }).execute()
      : board.items().withColumns([...COLS]).where(data.paper ? { issueMonth: { contains: data.month }, paper: data.paper } : { issueMonth: { contains: data.month } }).orderBy({ column: 'name', direction: 'asc' }).withPagination({ limit: 50 }).execute();
    const totalsP = data.cursor ? Promise.resolve([]) : board.aggregate().where({ issueMonth: { contains: data.month } }).groupBy('paper').sum('cost', 'total').countItems('count').execute();
    const copyP = data.cursor ? Promise.resolve([]) : board.aggregate().where({ issueMonth: { contains: data.month } }).groupBy('adCopy').countItems('count').execute();
    const [res, totals, copy] = await Promise.all([list, totalsP, copyP]);
    return { items: res.items, cursor: res.cursor ?? null, totals, copy };
  }));

const AdInput = z.object({
  name: z.string().min(1).max(120), issueMonth: z.string().min(3), paper: PAPER, adSize: z.string().max(80).optional(),
  cost: z.number().min(0).optional(), section: SECTION, specialSectionName: z.string().max(80).optional(),
  adCopy: COPY, billed: BILLED, notes: z.string().max(2000).optional(),
});

export const saveAd = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), id: z.string().optional(), ad: AdInput }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const board = new AdListBoard();
    const { ad } = data;
    const fields = { name: ad.name, issueMonth: ad.issueMonth, paper: ad.paper, adSize: ad.adSize ?? '', cost: ad.cost ?? 0, section: ad.section,
      specialSectionName: ad.section === 'Special Section' ? ad.specialSectionName ?? '' : '', adCopy: ad.adCopy, billed: ad.billed, notes: ad.notes ?? '' };
    if (data.id) { await board.item(data.id).update(fields).execute(); return { id: data.id }; }
    const item = await board.item().create(fields).execute();
    return { id: item.id };
  }));

export const updateAdField = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), id: z.string(), adCopy: COPY.optional(), billed: BILLED.optional(), adSize: z.string().max(80).optional(), cost: z.number().min(0).optional(), section: SECTION.optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const f: { adCopy?: z.infer<typeof COPY>; billed?: z.infer<typeof BILLED>; adSize?: string; cost?: number; section?: z.infer<typeof SECTION> } = {};
    if (data.adSize !== undefined) f.adSize = data.adSize;
    if (data.cost !== undefined) f.cost = data.cost;
    if (data.section) f.section = data.section;
    if (data.adCopy) f.adCopy = data.adCopy;
    if (data.billed) f.billed = data.billed;
    await new AdListBoard().item(data.id).update(f).execute();
    return { ok: true };
  }));

/** Copy last month's ads into this month (skips advertisers already on this month's list). */
export const repeatLastMonth = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), month: z.string().min(3), paper: PAPER.optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const board = new AdListBoard();
    const prev = addMonths(data.month, -1);
    const [last, cur] = await Promise.all([
      board.items().withColumns([...COLS]).where(data.paper ? { issueMonth: { contains: prev }, paper: data.paper } : { issueMonth: { contains: prev } }).withPagination({ limit: 100 }).execute(),
      board.items().withColumns(['paper']).where({ issueMonth: { contains: data.month } }).withPagination({ limit: 100 }).execute(),
    ]);
    const have = new Set(cur.items.map((i) => `${i.name.toLowerCase()}|${i.paper ?? ''}`));
    const todo = last.items.filter((i) => (i.issueMonth ?? '').trim() === prev && !have.has(`${i.name.toLowerCase()}|${i.paper ?? ''}`) && !(i.portalOrderId ?? '').trim());
    for (const i of todo) {
      await board.item().create({ name: i.name, issueMonth: data.month, paper: i.paper ?? 'Red Hook Star-Revue', adSize: i.adSize ?? '', cost: i.cost ?? 0,
        section: i.section ?? 'ROP', specialSectionName: '', adCopy: 'Same as Last Month', billed: 'Not Billed', notes: i.notes ?? '' }).execute();
    }
    return { added: todo.length, from: prev };
  }));

/** Copy a portal order into each issue month it runs in, then mark it copied. */
export const copyOrderToAdList = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), orderId: z.string().min(1) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const orders = new PortalOrdersBoard();
    const o = await orders.item(data.orderId).withColumns(['publication', 'adSize', 'duration', 'startMonth', 'totalContractValue', 'monthlyRate', 'adFiles', 'status']).execute();
    if (!o) throw new Error('Order not found.');
    const pub = (o.publication ?? '').toLowerCase();
    if (pub.includes('video only')) throw new Error('Video-only sponsorships don\'t go on the print ad list.');
    const papers: z.infer<typeof PAPER>[] = pub.includes('bundle') || pub.includes('both') || (pub.includes('red hook') && pub.includes('village'))
      ? ['Red Hook Star-Revue', 'The Village Star-Revue'] : pub.includes('village') ? ['The Village Star-Revue'] : ['Red Hook Star-Revue'];
    const months = Math.max(1, parseInt(o.duration ?? '1', 10) || 1);
    const start = o.startMonth ?? '';
    if (!start) throw new Error('This order has no start month.');
    const total = o.totalContractValue ?? (o.monthlyRate ?? 0) * months;
    const cost = Math.round((total / months / papers.length) * 100) / 100;
    const adList = new AdListBoard();
    const existing = await adList.items().withColumns(['portalOrderId']).where({ portalOrderId: { contains: data.orderId } }).withPagination({ limit: 1 }).execute();
    if (existing.items.length) throw new Error('This order is already on the Ad List.');
    const copy = (o.adFiles?.length ?? 0) > 0 ? 'Have Copy' : 'Need Copy';
    let added = 0;
    for (let m = 0; m < months; m++) {
      for (const paper of papers) {
        await adList.item().create({ name: o.name, issueMonth: addMonths(start, m), paper, adSize: o.adSize ?? '', cost, section: 'ROP',
          adCopy: m === 0 ? copy : copy === 'Have Copy' ? 'Same as Last Month' : 'Need Copy', billed: 'Not Billed', portalOrderId: data.orderId }).execute();
        added++;
      }
    }
    await orders.item(data.orderId).update({ status: 'Copied to Monthly List' }).execute();
    return { added, months: Array.from({ length: months }, (_, m) => addMonths(start, m)) };
  }));

/** One-time import of the old "October 2026, Red Hook" board into the Ad List. Skips advertisers already there. */
export const importOldOctober = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional() }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const old = new October2026RedHookBoard();
    const adList = new AdListBoard();
    const MONTH = 'October 2026';
    const PAPER_RH = 'Red Hook Star-Revue' as const;
    const rows = [];
    let res = await old.items().withColumns(['cost', 'adSize', 'invoiced', 'paid', 'copyIn', 'recurring', 'digital', 'billingInformation', 'pages']).withPagination({ limit: 100 }).execute();
    rows.push(...res.items);
    while (res.cursor) { res = await old.items().withPagination({ cursor: res.cursor }).execute(); rows.push(...res.items); }
    const cur = await adList.items().withColumns(['paper']).where({ issueMonth: { contains: MONTH } }).withPagination({ limit: 100 }).execute();
    const have = new Set(cur.items.filter((i) => i.paper === PAPER_RH).map((i) => i.name.trim().toLowerCase()));
    let added = 0;
    for (const r of rows) {
      if (have.has(r.name.trim().toLowerCase())) continue;
      const billed = r.paid === 'In Full' ? 'Paid' : r.invoiced === 'Yes' ? 'Invoiced' : 'Not Billed';
      const notes = [
        r.paid === 'Free' || r.invoiced === 'Free' ? 'Free ad' : '',
        r.recurring ? `Recurring: ${r.recurring}` : '',
        r.digital ? `Digital: ${r.digital}` : '',
        r.pages ? `Pages: ${r.pages}` : '',
        r.billingInformation ? `Billing: ${r.billingInformation}` : '',
      ].filter(Boolean).join('\n');
      await adList.item().create({ name: r.name, issueMonth: MONTH, paper: PAPER_RH, adSize: r.adSize ?? '', cost: r.cost ?? 0, section: 'ROP',
        adCopy: r.copyIn === 'Yes' ? 'Have Copy' : 'Need Copy', billed, notes }).execute();
      added++;
    }
    return { added, skipped: rows.length - added };
  }));

/** Staff: remove an ad from the list (e.g. last-minute cancellation). Archived in monday, so it can be restored there. */
export const deleteAd = createServerFn({ method: 'POST' })
  .validator(z.object({ pass: z.string().optional(), id: z.string().regex(/^\d+$/) }))
  .handler(async ({ data }) => withBoardAuth(async () => {
    await requireStaff(data.pass);
    const board = new AdListBoard();
    const ad = await board.item(data.id).withColumns(['portalOrderId', 'issueMonth']).execute();
    await rememberSkip(ad?.portalOrderId ?? null, ad?.issueMonth ?? null);
    try { await board.item(data.id).archive().execute(); }
    catch (e) {
      // Archiving isn't always permitted for the app; fall back to taking it off the month's list.
      console.error('[adlist] archive failed, removing from month instead', e);
      await board.item(data.id).update({ issueMonth: 'Cancelled', notes: `Cancelled from ${ad?.issueMonth ?? 'its issue'} on ${new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })}.` }).execute();
    }
    return { ok: true };
  }));
